import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X, Calendar, Clock, Users, ExternalLink, Trash2,
  AlertTriangle, Loader2, User, Repeat, Layers, Pencil, Share2, ArrowRight,
  Banknote, Hourglass,
} from 'lucide-react';
import { parseUtc } from '../lib/calendarDate';
import ShareModal from './ShareModal';
import AddToCalendarPopover from './AddToCalendarPopover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  private_1on1:  { label: 'Session privée 1-to-1', icon: User,   tint: 'violet' },
  public_slot:   { label: 'Créneau public',        icon: Repeat, tint: 'teal'   },
  public_event:  { label: 'Événement public',      icon: Users,  tint: 'amber'  },
  public_series: { label: 'Série récurrente',      icon: Layers, tint: 'cyan'   },
};

const TINT = {
  teal:   { bg: 'bg-cyan-500/10',   border: 'border-cyan-400/40',   text: 'text-cyan-300' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-400/40', text: 'text-violet-300' },
  amber:  { bg: 'bg-amber-500/10',  border: 'border-amber-400/40',  text: 'text-amber-300' },
  cyan:   { bg: 'bg-cyan-500/10',   border: 'border-cyan-400/40',   text: 'text-cyan-300' },
};

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Brief Jul 6 2026 P1 — liste des inscrits avec statut de paiement (owner only).
const PAYMENT_BADGE = {
  paid:    { label: 'Payé',       cls: 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30' },
  pending: { label: 'En attente', cls: 'bg-amber-500/15 text-amber-300 border border-amber-400/30' },
  free:    { label: 'Gratuit',    cls: 'bg-white/10 text-gray-300 border border-white/15' },
};

// Exporté — réutilisé par SessionDetailsDrawer (Audit Fable5 Jul 7 2026, Bug 7).
export const AttendeesSection = ({ eventId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/calendar/events/${eventId}/attendees`, { withCredentials: true })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [eventId]);

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-gray-400" data-testid="event-attendees-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement des inscrits…
      </div>
    );
  }
  if (!data) return null;
  const { attendees = [], summary = {} } = data;

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="event-attendees-section">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
          Inscrits ({summary.total || 0}{data.max_capacity ? `/${data.max_capacity}` : ''})
        </p>
        {(summary.revenue_paid > 0 || summary.revenue_pending > 0) && (
          <span className="text-[11px] text-emerald-300 font-medium" data-testid="event-attendees-revenue">
            <Banknote className="inline h-3 w-3 mr-0.5" />
            {summary.revenue_paid} CHF encaissés
            {summary.revenue_pending > 0 ? ` · ${summary.revenue_pending} CHF en attente` : ''}
          </span>
        )}
      </div>
      {attendees.length === 0 ? (
        <p className="text-xs text-gray-500" data-testid="event-attendees-empty">Aucun inscrit pour l&apos;instant.</p>
      ) : (
        <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {attendees.map((a) => {
            const badge = PAYMENT_BADGE[a.payment_status] || PAYMENT_BADGE.free;
            return (
              <li
                key={a.booking_id}
                className="flex items-center gap-2.5 p-2 rounded-lg bg-white/5 border border-white/10"
                data-testid={`event-attendee-${a.booking_id}`}
              >
                {a.avatar ? (
                  <img src={a.avatar} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-cyan-500/20 text-cyan-200 flex items-center justify-center text-[11px] font-semibold shrink-0">
                    {(a.display_name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white font-medium truncate">{a.display_name}</p>
                  {a.slot_start_time && (
                    <p className="text-[10px] text-gray-500 truncate">
                      Créneau : {parseUtc(a.slot_start_time).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.cls}`}
                  data-testid={`event-attendee-payment-${a.booking_id}`}
                >
                  {a.payment_status === 'pending' && <Hourglass className="inline h-2.5 w-2.5 mr-0.5" />}
                  {badge.label}{a.payment_status !== 'free' && a.price ? ` · ${a.price} CHF` : ''}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const EventDetailsModal = ({ event, isOpen, onClose, onChanged, onEdit, ownerUserId, variant = 'modal' }) => {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);  // Sprint 1.0c V1.a-bis hotfix #4 Bug 7

  if (!isOpen || !event) return null;

  const isOwner = ownerUserId && event.user_id === ownerUserId;
  const tc = TINT[TYPE_LABELS[event.type]?.tint || 'teal'];
  const Icon = TYPE_LABELS[event.type]?.icon || Calendar;
  const start = parseUtc(event.start_time);
  const end = parseUtc(event.end_time);

  // If this is a fused-slot proxy event, expose the underlying options
  const fusedOptions = event._fused_options || [];

  // Brief Phase B2 §1 — visitor CTA Réserver per session.
  // For a fused slot (multiple sessions on the same time), each option gets
  // its own "Réserver →" button. For solo public events / slots / series, the
  // visitor sees a single Réserver CTA pointing to /event/{id}?book=1.
  const isPublic = event.visibility === 'public' || ['public_slot', 'public_event', 'public_series'].includes(event.type);
  const isPast = start && start.getTime() < Date.now();
  const canVisitorBook = !isOwner && isPublic && !isPast && event.event_id;
  // Brief Phase B2 §5 — sub-slot booking (virtual). Detected via `_virtual` flag
  // set by GET /events/public/{user_id} when expanding availability_schedule.
  const isVirtualSlot = !!event._virtual;
  // Brief Jul 7 2026 — affichage prix visiteur : "X CHF" ou "Gratuit".
  const priceNum = Number(event.price) || 0;
  const isPaidEvent = !event.is_free && priceNum > 0;
  const goToBook = (eid, extra = '') => {
    if (!eid) return;
    onClose();
    navigate(`/event/${eid}?book=1${extra}`);
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.delete(`${API}/calendar/events/${event.event_id}`, { withCredentials: true });
      toast.success('Événement annulé.');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Annulation impossible.');
    } finally {
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  // Brief Jul 6 2026 P1 — variante "drawer" (panneau latéral style Outlook)
  // utilisée par le calendrier expert. Le modal centré reste le défaut ailleurs.
  const isDrawer = variant === 'drawer';

  return (
    <div
      className={isDrawer ? 'fixed inset-0 z-50' : 'fixed inset-0 z-50 flex items-center justify-center p-4'}
      data-testid={isDrawer ? 'event-details-drawer' : 'event-details-modal'}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={isDrawer
        ? 'absolute right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border-l border-white/10 shadow-2xl p-6 overflow-y-auto os-drawer-slide-in'
        : 'relative w-full max-w-lg rounded-2xl bg-gradient-to-br from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border border-white/10 shadow-2xl p-6'
      }>
        {/* Header */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${tc.bg} ${tc.text} flex-shrink-0`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <span className={`inline-block text-[10px] uppercase tracking-wide font-semibold ${tc.text}`}>
                {TYPE_LABELS[event.type]?.label}
              </span>
              <h3 className="text-lg font-semibold text-white truncate" data-testid="event-details-title">
                {event.title}
              </h3>
            </div>
          </div>
          <button
            data-testid="event-details-close"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-3 text-sm text-gray-300">
          {event.description && (
            <p className="text-gray-300/90 whitespace-pre-wrap">{event.description}</p>
          )}

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>
              {start?.toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span>
              Durée : {event.duration_minutes} min ({start?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} →{' '}
              {end?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
            </span>
          </div>

          {event.recurrence?.pattern === 'weekly' && event.recurrence.days_of_week?.length > 0 && (
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-gray-400" />
              <span>
                Tous les {event.recurrence.days_of_week.map((d) => DAY_NAMES[d]?.toLowerCase()).join(', ')}
                {event.recurrence.occurrences_count > 1 && ` · ${event.recurrence.occurrences_count} séances`}
              </span>
            </div>
          )}

          {event.type === 'public_event' && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span>Capacité : {event.max_capacity} participant(s)</span>
            </div>
          )}

          {event.type === 'private_1on1' && (
            <div className="p-3 rounded-lg bg-violet-500/5 border border-violet-400/20 text-xs space-y-1">
              <p className="text-violet-200">
                Invité : <strong>{event.invitation_email || event.invited_user_id || '—'}</strong>
              </p>
              <p className="text-violet-300/70">
                Statut : {event.invitation_status === 'accepted'
                  ? 'Accepté ✓'
                  : event.invitation_status === 'declined'
                  ? 'Refusé'
                  : 'En attente'}
              </p>
            </div>
          )}

          {fusedOptions.length > 0 && (
            <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-400/20 text-xs" data-testid="event-details-fused-options">
              <p className="text-cyan-200 font-medium mb-1.5">
                {fusedOptions.length} sessions au choix sur ce créneau :
              </p>
              {/* Brief Phase B2 §1 — visiteurs : 1 carte "Réserver" par session.
                  Owner : on retombe sur la liste minimaliste pour ne pas polluer
                  son flow de gestion. */}
              {!isOwner ? (
                <ul className="space-y-2 mt-2">
                  {fusedOptions.map((opt) => {
                    const optPrice = Number(opt.price) || 0;
                    const optPaid = !opt.is_free && optPrice > 0;
                    return (
                      <li
                        key={opt.event_id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md bg-cyan-500/10 border border-cyan-400/15"
                        data-testid={`fused-option-${opt.event_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-cyan-100 text-sm font-medium truncate">{opt.title}</p>
                          <p className="text-cyan-300/70 text-[11px]" data-testid={`fused-option-price-${opt.event_id}`}>
                            {opt.duration_minutes} min · {optPaid ? `${optPrice} ${opt.currency || 'CHF'}` : 'Gratuit'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => goToBook(opt.event_id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-white text-xs font-medium whitespace-nowrap ${
                            optPaid ? 'bg-purple-500/80 hover:bg-purple-500' : 'bg-cyan-500/80 hover:bg-cyan-500'
                          }`}
                          data-testid={`fused-option-book-${opt.event_id}`}
                        >
                          {optPaid ? `Réserver · ${optPrice} ${opt.currency || 'CHF'}` : 'Réserver'} <ArrowRight className="h-3 w-3" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className="space-y-1 list-disc list-inside text-cyan-100/80">
                  {fusedOptions.map((opt) => (
                    <li key={opt.event_id}>{opt.title} ({opt.duration_minutes} min)</li>
                  ))}
                </ul>
              )}
            </div>
          )}

        {/* Brief Jul 7 2026 — visio réservée au propriétaire : les visiteurs non
            inscrits ne voient plus ce bouton (le lien arrive après confirmation). */}
        {isOwner && event.visio_link && (
            <button
              type="button"
              data-testid="event-details-jitsi"
              onClick={async () => {
                // Sprint 1.0c V1.a-bis hotfix #4 Bug 8B — open Jitsi pre-filled with the user's display name
                try {
                  const { data } = await axios.get(
                    `${API}/calendar/events/${event.event_id}/jitsi-link`,
                    { withCredentials: true }
                  );
                  window.open(data.jitsi_url || event.visio_link, '_blank', 'noopener,noreferrer');
                } catch {
                  window.open(event.visio_link, '_blank', 'noopener,noreferrer');
                }
              }}
              className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 underline text-xs"
            >
              <ExternalLink className="h-3 w-3" /> Rejoindre la visio
            </button>
          )}
        </div>

        {/* Brief Jul 6 2026 P1 — liste détaillée des inscrits + statuts paiement (owner) */}
        {isOwner && !event._fused && event.event_id && (
          <AttendeesSection eventId={event.event_id} />
        )}

        {/* Actions */}
        {/* Brief Phase B2 §1 & §4 — visitor actions on a public event/slot/series.
            For fused slots, the per-session CTA already lives inside the body
            (see fused-option block above) and we skip the global Réserver. */}
        {!isOwner && canVisitorBook && fusedOptions.length === 0 && (
          <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
            {/* Brief Jul 7 2026 — prix affiché clairement au-dessus du CTA */}
            <div className="text-center pb-1" data-testid="event-details-price">
              {isPaidEvent ? (
                <p className="text-2xl font-bold text-purple-300" data-testid="event-details-price-paid">
                  {priceNum} {event.currency || 'CHF'}
                </p>
              ) : (
                <p className="text-2xl font-bold text-emerald-300" data-testid="event-details-price-free">
                  Gratuit
                </p>
              )}
            </div>
            <button
              type="button"
              data-testid={isVirtualSlot ? 'event-details-virtual-book-btn' : 'event-details-visitor-book-btn'}
              onClick={() => {
                if (isVirtualSlot) {
                  const startIso = event.slot_start_time
                    || (event.start_time instanceof Date ? event.start_time.toISOString() : event.start_time);
                  goToBook(event.event_id, `&slot=${encodeURIComponent(startIso)}`);
                } else {
                  goToBook(event.event_id);
                }
              }}
              className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-white text-sm font-semibold ${
                isPaidEvent ? 'bg-purple-500/90 hover:bg-purple-400' : 'bg-cyan-500/90 hover:bg-cyan-400'
              }`}
            >
              {isPaidEvent ? `Réserver · ${priceNum} ${event.currency || 'CHF'}` : 'Réserver gratuitement'} <ArrowRight className="h-4 w-4" />
            </button>
            {/* Audit Fable5 Jul 7 2026 (Bug 1) — "Ajouter au calendrier" retiré
                pour les visiteurs non inscrits : il n'apparaît qu'après
                réservation confirmée (Mes sessions / confirmation). */}
          </div>
        )}

        {/* Actions */}
        {isOwner && !event._fused && (
          <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
            {/* Brief Phase B2 §4 — popover Ajouter au calendrier (OS + ICS).
                Remplace l'ancien lien ICS direct. Le bouton garde un look
                "owner" cohérent avec le reste du modal. */}
            <AddToCalendarPopover
              event={{ ...event, ics_token: event.ics_token }}
              source="event-details-modal-owner"
              triggerClassName="block w-full text-center px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-cyan-300 hover:bg-white/10"
              testIdPrefix="event-details-add-calendar-owner"
            />

            {/* Sprint 1.0c V1.a-bis hotfix #4 Bug 7 — share event into Actualité / DM */}
            {event.type !== 'private_1on1' && (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                data-testid="event-details-share-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-400/30 text-violet-200 text-xs hover:bg-violet-500/20"
              >
                <Share2 className="h-3.5 w-3.5" />
                Partager
              </button>
            )}

            <button
              data-testid="event-details-edit-btn"
              type="button"
              onClick={() => onEdit?.(event)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-200 text-xs hover:bg-cyan-500/20"
            >
              <Pencil className="h-3.5 w-3.5" />
              Modifier
            </button>

            {!confirmCancel ? (
              <button
                data-testid="event-details-cancel-btn"
                type="button"
                onClick={() => setConfirmCancel(true)}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 border border-red-400/30 text-red-200 text-xs hover:bg-red-500/20"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Annuler cet événement
              </button>
            ) : (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-300 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-200">
                    Cette action est <strong>irréversible</strong>. Les réservations existantes seront
                    annulées et les apprenants devront être prévenus manuellement.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(false)}
                    disabled={cancelling}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs"
                  >
                    Garder
                  </button>
                  <button
                    data-testid="event-details-confirm-cancel"
                    type="button"
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/80 text-white text-xs font-medium hover:bg-red-500 disabled:opacity-60"
                  >
                    {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    {cancelling ? 'Annulation…' : 'Confirmer'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sprint 1.0c V1.a-bis hotfix #5 (Bug L) — message admin réservé au propriétaire.
            Avant : visible par tout visiteur d'un créneau fusionné → leak UX + admin info.
            Après : strict isOwner. */}
        {event._fused && isOwner && (
          <div className="mt-5 pt-4 border-t border-white/10 text-xs text-gray-400 text-center">
            Pour modifier une session précise de ce créneau, désactivez la fusion des slots empilés.
          </div>
        )}
      </div>

      {/* Sprint 1.0c V1.a-bis hotfix #4 Bug 7 — Share modal mounted at the modal root */}
      {shareOpen && (
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          shareType="event"
          shareData={{
            id: event.event_id,
            userId: ownerUserId || event.user_id,
            title: event.title,
            description: event.description || `Le ${parseUtc(event.start_time).toLocaleDateString('fr-FR')}`,
          }}
          onShareToFeed={async (message) => {
            await axios.post(
              `${API}/calendar/events/${event.event_id}/share-to-feed`,
              { message },
              { withCredentials: true }
            );
            toast.success('Événement publié dans Actualité');
          }}
        />
      )}
    </div>
  );
};

export default EventDetailsModal;
